import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link } from "react-router-dom";
import { slugify } from "../../utils/slugify";
import { getRequest } from "../../services/api";
import "./OnlineTestSeries.css";

interface BlogPost {
  _id: string;
  slug: string;
  header_title: string;
  url_title: string;
  category_name: string;
  test_category_name: string;
  sub_category_name: string;
  sub_heading: string;
  total_tests: string;
  total_students: string;
  language: string;
  image: string;
  posted_by: string;
  updated_at: string;
  views: number;
  likes: number;
  test_series_type: string;
}

/* ─── Inline SVGs ──────────────────────────────────────────────────────────── */
const IconBook = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
  </svg>
);
const IconGrad = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);
// const IconFile = () => (
//   <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
//     <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
//     <polyline points="14 2 14 8 20 8" />
//     <line x1="16" y1="13" x2="8" y2="13" />
//     <line x1="16" y1="17" x2="8" y2="17" />
//     <polyline points="10 9 9 9 8 9" />
//   </svg>
// );
// const IconEye = () => (
//   <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
//     <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
//     <circle cx="12" cy="12" r="3" />
//   </svg>
// );

/* ─── ExamCard ─────────────────────────────────────────────────────────────── */
interface ExamCardProps {
  test: BlogPost;
  linkPrefix: string;
  iconType?: "book" | "grad";
  levelClass?: string;
  levelLabel?: string;
}
const ExamCard: React.FC<ExamCardProps> = ({
  test, linkPrefix, iconType = "book",
  levelClass = "level-advanced", levelLabel = "Advanced"
}) => (
  <Link to={`${linkPrefix}/${slugify(test.sub_category_name)}`} className="ots-card-link">
    <div className="ots-exam-card">
      <div className="ots-card-header">
        <div className="ots-card-title-row">
          <div className="ots-card-icon">
            {iconType === "grad" ? <IconGrad /> : <IconBook />}
          </div>
          <div>
            <div className="ots-card-name">{test.sub_category_name}</div>
            <div className="ots-card-sub">{test.sub_heading}</div>
          </div>
        </div>
        <span className="ots-card-arrow">›</span>
      </div>
      <div className="ots-card-footer">
        <div className="ots-card-meta">
          <span className="ots-meta-item"><IconBook />{test.total_tests} Tests</span>
          <span className="ots-meta-item"><IconClock />{test.language}</span>
          <span className="ots-meta-item"><IconUser />{test.total_students}</span>
        </div>
        <span className={`ots-level-badge ${levelClass}`}>{levelLabel}</span>
      </div>
    </div>
  </Link>
);

/* ─── Skeleton cards ───────────────────────────────────────────────────────── */
const SkeletonCards = () => (
  <>
    {[1, 2, 3, 4].map(n => (
      <div className="ots-exam-card" key={n} style={{ gap: 12 }}>
        <Skeleton height={22} width="55%" />
        <Skeleton height={15} width="35%" />
        <Skeleton height={15} width="75%" style={{ marginTop: 10 }} />
      </div>
    ))}
  </>
);

/* ─── Static MCQ sidebar data ──────────────────────────────────────────────── */
// const MCQ_ITEMS = [
//   { title: "JEE Main Physics MCQs - Wave Optics",        qs: "25 Qs", views: "12.5K", tagClass: "ots-tag-physics",     tag: "Physics",   time: "2 days ago" },
//   { title: "NEET Biology - Cell Structure and Function", qs: "30 Qs", views: "18.2K", tagClass: "ots-tag-biology",     tag: "Biology",   time: "3 days ago" },
//   { title: "SSC CGL Reasoning Ability Questions",        qs: "40 Qs", views: "9.8K",  tagClass: "ots-tag-reasoning",   tag: "Reasoning", time: "5 days ago" },
//   { title: "IBPS PO Quantitative Aptitude Practice",     qs: "35 Qs", views: "15.3K", tagClass: "ots-tag-mathematics", tag: "Maths",     time: "1 week ago" },
//   { title: "Class 12 Chemistry - Organic Reactions",     qs: "20 Qs", views: "11.7K", tagClass: "ots-tag-chemistry",   tag: "Chemistry", time: "1 week ago" },
// ];

/* ─── Main Component ───────────────────────────────────────────────────────── */
const OnlineTestSeries: React.FC = () => {
  const [examWise,      setExamWise]      = useState<BlogPost[]>([]);
  const [classWise,     setClassWise]     = useState<BlogPost[]>([]);
  const [topicWise,     setTopicWise]     = useState<BlogPost[]>([]);
  const [examCount,     setExamCount]     = useState<number>(0);
  const [classCount,    setClassCount]    = useState<number>(0);
  const [topicCount,    setTopicCount]    = useState<number>(0);
  const [loading,       setLoading]       = useState<boolean>(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [eRes, cRes, tRes] = await Promise.all([
          getRequest("/test-series", { category: "Test Series", test_series_type: "exam_wise",  limit: 6 }),
          getRequest("/test-series", { category: "Test Series", test_series_type: "class_wise", limit: 6 }),
          getRequest("/test-series", { category: "Test Series", test_series_type: "topic_wise", limit: 6 }),
        ]);
        setExamWise(eRes.data.testSeries);   setExamCount(eRes.data.totalCount);
        setClassWise(cRes.data.testSeries);  setClassCount(cRes.data.totalCount);
        setTopicWise(tRes.data.testSeries);  setTopicCount(tRes.data.totalCount);
      } catch (err) {
        console.error("Error fetching test series:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  return (
    <>
      <Helmet>
        <title>Acadebility - All Online Test series</title>
        <meta name="description" content="All Online Test series" />
      </Helmet>

      {/*
        ┌─ .ots-page ──────────────────────────────────────────────────────────┐
        │  All classes are prefixed "ots-" so Bootstrap CANNOT match them.     │
        │  No Bootstrap container / row / col anywhere in this tree.           │
        └──────────────────────────────────────────────────────────────────────┘
      */}
      <div className="ots-page">

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <section className="ots-hero">
          <div className="ots-hero-text">
            <h1>Master Your Exams with Expert Test Series</h1>
            <p>
              Practice with thousands of questions across JEE, NEET, UPSC,
              Banking, SSC, and more. Track your progress and achieve your goals.
            </p>
            <div className="ots-hero-btns">
              <button type="button" className="ots-btn-primary">Start Free Trial</button>
              <button type="button" className="ots-btn-outline">Browse Tests</button>
            </div>
          </div>

          <div className="ots-hero-stats">
            <div className="ots-stat-card">
              <div className="ots-stat-icon"><IconBook /></div>
              <div className="ots-stat-info">
                <div className="ots-stat-num">5000+</div>
                <div className="ots-stat-label">Questions</div>
              </div>
            </div>
            <div className="ots-stat-card">
              <div className="ots-stat-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="ots-stat-info">
                <div className="ots-stat-num">400+</div>
                <div className="ots-stat-label">Online Tests</div>
              </div>
            </div>
            <div className="ots-stat-card">
              <div className="ots-stat-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                  <circle cx="12" cy="8" r="6" />
                  <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
                </svg>
              </div>
              <div className="ots-stat-info">
                <div className="ots-stat-num">95%</div>
                <div className="ots-stat-label">Success Rate</div>
              </div>
            </div>
            <div className="ots-stat-card">
              <div className="ots-stat-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                  <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                  <polyline points="16 7 22 7 22 13" />
                </svg>
              </div>
              <div className="ots-stat-info">
                <div className="ots-stat-num">4.8/5</div>
                <div className="ots-stat-label">Avg Rating</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PAGE BODY: two columns via CSS grid ───────────────────────────── */}
        {/*
          CRITICAL: this div has NO Bootstrap col/row wrappers.
          CSS does:  display:grid; grid-template-columns: 1fr 320px;
          Left  → .ots-main   (exam/class/topic sections)
          Right → .ots-sidebar (MCQ posts)
        */}
        <div className="ots-body no-sidebar">

          {/* LEFT — main content */}
          <div className="ots-main">

            {/* EXAM WISE */}
            <div className="ots-section">
              <div className="ots-section-header">
                <div>
                  <h2>Exam Wise Test Series</h2>
                  <p>Prepare for competitive exams with specialized test series</p>
                </div>
                <span className="ots-badge-pill">{examCount} Exams</span>
              </div>
              <div className="ots-cards-grid three-columns">
                {loading
                  ? <SkeletonCards />
                  : examWise.map((test, i) => (
                      <ExamCard key={test._id || i} test={test}
                        linkPrefix="/online-test-series/exam-wise" iconType="book"
                        levelClass="level-advanced" levelLabel="Advanced" />
                    ))}
              </div>
              <div className="ots-view-all-wrap">
                <button type="button" className="ots-btn-view-all">View All Exams →</button>
              </div>
            </div>

            {/* CLASS WISE */}
            <div className="ots-section">
              <div className="ots-section-header">
                <div>
                  <h2>Class Wise Test Series</h2>
                  <p>Grade-specific practice tests for school students</p>
                </div>
                <span className="ots-badge-pill">{classCount} Classes</span>
              </div>
              <div className="ots-cards-grid three-columns">
                {loading
                  ? <SkeletonCards />
                  : classWise.map((test, i) => (
                      <ExamCard key={test._id || i} test={test}
                        linkPrefix="/online-test-series/class-wise" iconType="grad"
                        levelClass="level-expert" levelLabel="Expert" />
                    ))}
              </div>
            </div>

            {/* TOPIC WISE */}
            <div className="ots-section">
              <div className="ots-section-header">
                <div>
                  <h2>Topic Wise Test Series</h2>
                  <p>Master individual subjects with focused practice</p>
                </div>
                <span className="ots-badge-pill">{topicCount} Subjects</span>
              </div>
              <div className="ots-cards-grid three-columns">
                {loading
                  ? <SkeletonCards />
                  : topicWise.map((test, i) => (
                      <ExamCard key={test._id || i} test={test}
                        linkPrefix="/online-test-series/topic-wise" iconType="book"
                        levelClass="level-intermediate" levelLabel="Intermediate" />
                    ))}
              </div>
            </div>

          </div>{/* /ots-main */}

          {/* RIGHT — sticky sidebar */}
          {/* <aside className="ots-sidebar">
            <div className="ots-sidebar-title">Recent MCQ Posts</div>
            <div className="ots-mcq-list">
              {MCQ_ITEMS.map((item) => (
                <div className="ots-mcq-card" key={item.title}>
                  <div className="ots-mcq-icon"><IconFile /></div>
                  <div className="ots-mcq-info">
                    <div className="ots-mcq-title">{item.title}</div>
                    <div className="ots-mcq-meta">
                      <span><IconFile />{item.qs}</span>
                      <span><IconEye />{item.views}</span>
                    </div>
                    <div className="ots-mcq-footer">
                      <span className={`ots-tag-subject ${item.tagClass}`}>{item.tag}</span>
                      <span className="ots-mcq-time">
                        <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" width="11" height="11">
                          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {item.time}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </aside> */}

        </div>{/* /ots-body */}
      </div>{/* /ots-page */}
    </>
  );
};

export default OnlineTestSeries;
